import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload, IconEdit, IconTrash } from '../components/icons';
import SearchableSelect from '../components/SearchableSelect';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

// TS port of the source (Falah) app's pages/CustomerQuotationDetail.jsx.
// Editing happens inline here (toggled by `editing` state, or by landing on
// `?edit=1` from the list page) rather than on a separate route — the
// source app's own convention, kept as-is. The backend only allows editing
// while status is 'draft' (CustomerQuotationController::update()); the Edit
// button is still always shown (matching source) and a 422 surfaces via
// `errors.general` if attempted outside that state.
function statusBadgeClass(status: string): string {
  if (['accepted', 'converted_to_invoice'].includes(status)) return 'badge-normal';
  if (['rejected', 'expired'].includes(status)) return 'badge-important';
  return 'badge-neutral';
}

interface QuotationFormState {
  customer_id: string | number;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  service: string;
  quotation_date: string;
  amount: string | number;
  valid_until: string;
  notes: string;
  status: string;
}

const emptyForm: QuotationFormState = {
  customer_id: '',
  customer_name: '',
  customer_phone: '',
  customer_email: '',
  service: '',
  quotation_date: '',
  amount: '',
  valid_until: '',
  notes: '',
  status: 'draft',
};

interface CustomerOption {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  [key: string]: unknown;
}

interface QuotationRecord {
  id: number;
  quotation_number: string;
  customer_id: number | null;
  customer: { id: number; name: string } | null;
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  service: string;
  quotation_date: string;
  amount: number;
  valid_until: string | null;
  notes: string | null;
  status: string;
  invoice: { id: number; invoice_number: string } | null;
}

type FormErrors = Record<string, string[]>;

export function CustomerQuotationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { settings, lists } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const [quotation, setQuotation] = useState<QuotationRecord | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<QuotationFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [actionError, setActionError] = useState('');

  const refresh = async () => {
    const res = await api.get<QuotationRecord>(`/customer-quotations/${id}`);
    setQuotation(res.data);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const startEdit = (current: QuotationRecord) => {
    setForm({
      customer_id: current.customer_id ?? '',
      customer_name: current.customer_name,
      customer_phone: current.customer_phone ?? '',
      customer_email: current.customer_email ?? '',
      service: current.service,
      quotation_date: current.quotation_date,
      amount: current.amount,
      valid_until: current.valid_until ?? '',
      notes: current.notes ?? '',
      status: current.status,
    });
    setErrors({});
    setEditing(true);
  };

  useEffect(() => {
    if (quotation && searchParams.get('edit') === '1') {
      startEdit(quotation);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotation]);

  const set =
    (key: keyof QuotationFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const selectCustomer = (id: string | number, customer?: CustomerOption) => {
    setForm({
      ...form,
      customer_id: id,
      customer_name: customer?.name ?? form.customer_name,
      customer_phone: customer?.phone ?? form.customer_phone,
      customer_email: customer?.email ?? form.customer_email,
    });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.put(`/customer-quotations/${id}`, form);
      setEditing(false);
      await refresh();
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    }
  };

  const runAction = async (fn: () => Promise<void>) => {
    setActionError('');
    try {
      await fn();
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Something went wrong.');
    }
  };

  const convertToInvoice = () =>
    runAction(async () => {
      const res = await api.post<{ id: number }>(`/customer-quotations/${id}/convert-to-invoice`);
      navigate(`/revenue/invoices/${res.data.id}`);
    });

  const download = () =>
    quotation && downloadFile(api, `/customer-quotations/${id}/pdf`, `${quotation.quotation_number}.pdf`);

  const remove = async () => {
    if (!quotation) return;
    if (!(await confirmDelete(`Delete quotation ${quotation.quotation_number}?`))) return;
    try {
      await api.delete(`/customer-quotations/${id}`);
      navigate('/revenue/quotations');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!quotation) return null;

  return (
    <>
      <header className="page-header">
        {editing && <h1>Quotation: {quotation.quotation_number}</h1>}
        {!editing && (
          <div className="page-header-actions">
            <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/revenue/quotations')} />
            <IconButton icon={IconDownload} label="Download PDF" onClick={download} />
            <IconButton icon={IconEdit} label="Edit details" onClick={() => startEdit(quotation)} />
            <IconButton
              icon={IconTrash}
              label="Delete"
              variant="danger-ghost"
              onClick={remove}
              hidden={quotation.status !== 'draft'}
            />
          </div>
        )}
      </header>

      <div className="card">
        {!editing ? (
          <div>
            {actionError && <div className="alert alert-error">{actionError}</div>}

            <div className="detail-summary">
              <div>
                <div className="detail-summary-title">{quotation.quotation_number}</div>
                <span className={`badge ${statusBadgeClass(quotation.status)}`}>
                  {labelFor(lists, 'quotation_status', quotation.status)}
                </span>
                <div className="detail-summary-amount">{fmtMoney(quotation.amount)}</div>
                <div className="detail-summary-service">{quotation.service}</div>
              </div>
              <div>
                {quotation.status === 'accepted' && !quotation.invoice && (
                  <button type="button" className="btn btn-primary" onClick={convertToInvoice}>
                    Convert to invoice
                  </button>
                )}
                {quotation.invoice && (
                  <Link className="btn btn-ghost" to={`/revenue/invoices/${quotation.invoice.id}`}>
                    View invoice {quotation.invoice.invoice_number}
                  </Link>
                )}
              </div>
            </div>

            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-item-label">Customer name</span>
                {quotation.customer ? (
                  <Link className="detail-item-value" to={`/revenue/customers/${quotation.customer.id}`}>
                    {quotation.customer_name}
                  </Link>
                ) : (
                  <span className="detail-item-value">{quotation.customer_name}</span>
                )}
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Customer phone</span>
                <span className={`detail-item-value${quotation.customer_phone ? '' : ' empty'}`}>
                  {quotation.customer_phone || 'Not provided'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Customer email</span>
                <span className={`detail-item-value${quotation.customer_email ? '' : ' empty'}`}>
                  {quotation.customer_email || 'Not provided'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Service</span>
                <span className="detail-item-value">{quotation.service}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Amount ({settings.currency})</span>
                <span className="detail-item-value">{fmtMoney(quotation.amount)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Quotation date</span>
                <span className="detail-item-value">{quotation.quotation_date}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Valid until</span>
                <span className={`detail-item-value${quotation.valid_until ? '' : ' empty'}`}>
                  {quotation.valid_until || 'Not set'}
                </span>
              </div>
            </div>

            <div className="detail-item detail-item-full">
              <span className="detail-item-label">Notes</span>
              {quotation.notes ? (
                <p className="detail-notes">{quotation.notes}</p>
              ) : (
                <span className="detail-item-value empty">Not provided</span>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Customer
              <SearchableSelect<CustomerOption>
                endpoint="/customers"
                value={form.customer_id}
                onChange={(id, customer) => selectCustomer(id, customer)}
                getLabel={(c) => c.name}
                getSubLabel={(c) => c.email || c.phone || ''}
                placeholder="Search customers…"
              />
              <small className="muted">Selecting a customer fills in the name, phone, and email below.</small>
            </label>
            <label className="span-2">
              Customer name *
              <input value={form.customer_name} onChange={set('customer_name')} required />
              {errors.customer_name && <small className="error">{errors.customer_name[0]}</small>}
            </label>
            <label>
              Customer phone
              <input type="tel" value={form.customer_phone} onChange={set('customer_phone')} />
              {errors.customer_phone && <small className="error">{errors.customer_phone[0]}</small>}
            </label>
            <label>
              Customer email
              <input type="email" value={form.customer_email} onChange={set('customer_email')} />
              {errors.customer_email && <small className="error">{errors.customer_email[0]}</small>}
            </label>
            <label className="span-2">
              Service *
              <input value={form.service} onChange={set('service')} required />
              {errors.service && <small className="error">{errors.service[0]}</small>}
            </label>
            <label>
              Status
              <select value={form.status} onChange={set('status')}>
                {listOptions(lists, 'quotation_status').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.status && <small className="error">{errors.status[0]}</small>}
            </label>
            <label>
              Amount ({settings.currency}) *
              <input type="number" step="0.01" min="0.01" value={form.amount} onChange={set('amount')} required />
              {errors.amount && <small className="error">{errors.amount[0]}</small>}
            </label>
            <label>
              Quotation date *
              <input type="date" value={form.quotation_date} onChange={set('quotation_date')} required />
            </label>
            <label>
              Valid until
              <input type="date" value={form.valid_until} onChange={set('valid_until')} />
            </label>
            <label className="span-2">
              Notes
              <textarea rows={2} value={form.notes} onChange={set('notes')} />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
